/**
 * Authentication Service
 * All auth-related API calls
 */
import api from './api';

const authService = {
  /**
   * Register new candidate
   */
  async registerCandidate(candidateData) {
    // Create FormData to handle file uploads
    const formData = new FormData();
    
    // Append text fields
    Object.keys(candidateData).forEach(key => {
      // Handle file fields
      if (key === 'resume' && candidateData[key]) {
        formData.append('resume', candidateData[key]);
      } else if (key === 'documents' && Array.isArray(candidateData[key])) {
        candidateData[key].forEach((file) => {
          if (file) {
            formData.append('documents', file);
          }
        });
      } else if (
        [
          'passportDocument',
          'stateRegistrationDocument',
          'practicingLicenseDocument',
          'degreeDiplomaDocument',
          'transcriptDocument',
          'schoolCertificatesDocument',
          'experienceCertificatesDocument',
          'marriageBirthCertificateDocument',
          'aadharCardDocument',
          'affidavitsDocument'
        ].includes(key) && candidateData[key]
      ) {
        formData.append(key, candidateData[key]);
      } else if (key === 'combinedDocuments' && candidateData[key]) {
        formData.append('combinedDocuments', candidateData[key]);
      } else if (key === 'profilePhoto' && candidateData[key]) {
        formData.append('profilePhoto', candidateData[key]);
      } 
      // Legacy support
      else if (key === 'resumeFile' && candidateData[key]) {
        formData.append('resume', candidateData[key]);
      } else if (key === 'documentsFile' && candidateData[key]) {
        formData.append('documents', candidateData[key]);
      } 
      // Append other non-file fields
      else if (candidateData[key] !== null && 
               key !== 'resume' && 
               key !== 'documents' && 
               key !== 'passportDocument' &&
               key !== 'stateRegistrationDocument' &&
               key !== 'practicingLicenseDocument' &&
               key !== 'degreeDiplomaDocument' &&
               key !== 'transcriptDocument' &&
               key !== 'schoolCertificatesDocument' &&
               key !== 'experienceCertificatesDocument' &&
               key !== 'marriageBirthCertificateDocument' &&
               key !== 'aadharCardDocument' &&
               key !== 'affidavitsDocument' &&
               key !== 'combinedDocuments' && 
               key !== 'profilePhoto' && 
               key !== 'resumeFile' && 
               key !== 'documentsFile') {
        formData.append(key, candidateData[key]);
      }
    });
    
    const response = await api.post('/auth/register-candidate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Login candidate with email and password
   */
  async loginCandidateWithPassword(email, password) {
    const response = await api.post('/auth/login-candidate', { email, password });
    return response.data;
  },

  /**
   * Set password for candidate who has no password set yet
   */
  async setCandidatePassword(email, password) {
    const response = await api.post('/auth/set-candidate-password', { email, password });
    return response.data;
  },

  /**
   * Reset candidate password using OTP verification
   */
  async resetCandidatePassword(email, otp, newPassword) {
    const response = await api.post('/auth/reset-candidate-password', { email, otp, newPassword });
    return response.data;
  },

  /**
   * Request password reset for HR/HRO user
   * Supports both phone number and email
   */
  async requestPasswordReset(employeeId, phoneNumber, email) {
    const response = await api.post('/auth/forgot-password', { employeeId, phoneNumber, email });
    return response.data;
  },

  /**
   * Reset password using reset token
   */
  async resetPassword(token, newPassword) {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  /**
   * Reset staff (HR/HRO/ADMIN/CHAIRMAN) password using OTP
   */
  async resetStaffPassword(email, otp, newPassword) {
    const response = await api.post('/auth/reset-staff-password', { email, otp, newPassword });
    return response.data;
  },

  /**
   * Login HR/HRO employee with employee ID and password
   */
  async loginHREmployee(employeeId, password) {
    const response = await api.post('/auth/login-hr', { employeeId, password });
    return response.data;
  },

  /**
   * Login HR/HRO employee with phone number and password
   */
  async loginHREmployeeByPhone(phoneNumber, password) {
    console.log('🔥 loginHREmployeeByPhone called with:', { phoneNumber, passwordLength: password.length });
    const response = await api.post('/auth/login-hr-phone', { phoneNumber, password });
    console.log('🔥 loginHREmployeeByPhone response:', response.data);
    return response.data;
  },

  /**
   * Login candidate with Google credential
   */
  async loginWithGoogle(credential) {
    const response = await api.post('/auth/google', { credential });
    return response.data;
  },

  /**
   * Send OTP to email for pre-registration verification
   */
  async sendRegistrationOtp(email) {
    const response = await api.post('/auth/send-registration-otp', { email });
    return response.data;
  },

  /**
   * Verify pre-registration email OTP (does not log in)
   */
  async verifyRegistrationOtp(email, otp) {
    const response = await api.post('/auth/verify-registration-otp', { email, otp });
    return response.data;
  },

  /**
   * Send OTP to phone number (legacy)
   */
  async sendOTP(phoneNumber) {
    const response = await api.post('/auth/send-otp', { phoneNumber });
    return response.data;
  },

  /**
   * Send OTP to email address
   */
  async sendEmailOTP(email) {
    const response = await api.post('/auth/send-otp', { email });
    return response.data;
  },

  /**
   * Verify OTP and get tokens (legacy phone-based)
   */
  async verifyOTP(phoneNumber, otp) {
    const response = await api.post('/auth/verify-otp', { phoneNumber, otp });
    return response.data;
  },

  /**
   * Verify email OTP and get tokens
   */
  async verifyEmailOTP(email, otp) {
    const response = await api.post('/auth/verify-otp', { email, otp });
    return response.data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken) {
    const response = await api.post('/auth/refresh-token', { refreshToken });
    return response.data;
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Logout
   */
  async logout() {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  /**
   * Get user statistics (ADMIN only)
   */
  async getUserStats() {
    const response = await api.get('/auth/stats');
    return response.data;
  }
};

export default authService;
